import { useMemo, useState, type ReactNode } from 'react';

import { homeworkListDate, type HomeworkListStatus } from '../domain/homework-list-presentation';

export interface HomeworkListRow {
  readonly id: string;
  readonly title: string;
  readonly classId?: string;
  readonly className?: string;
  readonly date: string | null;
  readonly dueAt: string | null;
  readonly metadata: string;
  readonly status: HomeworkListStatus;
  readonly lifecycle?: string | undefined;
  readonly progress: {
    readonly label: string;
    readonly value: number;
    readonly total: number;
  } | null;
  readonly action: string;
}

const PAGE_SIZE = 6;

function HomeworkDate({ value, label }: { readonly value: string | null; readonly label: string }) {
  const formatted = homeworkListDate(value);
  return formatted === null ? (
    <span className="homework-list__missing">{label}未记录</span>
  ) : (
    <time dateTime={value!} title={`${label}：${formatted.date} ${formatted.time}（北京时间）`}>
      <span>{formatted.date}</span>
      <small>{formatted.time}</small>
    </time>
  );
}

export function HomeworkListPanel({
  rows,
  statuses,
  dateLabel,
  description,
  loading,
  busy,
  error,
  onOpen,
  onDelete,
  onRefresh,
  onCancel,
  emptyTitle,
  classOptions,
}: {
  readonly rows: readonly HomeworkListRow[];
  readonly statuses: readonly HomeworkListStatus[];
  readonly dateLabel: string;
  readonly description: string;
  readonly loading: boolean;
  readonly busy: boolean;
  readonly error?: ReactNode;
  readonly onOpen: (id: string) => void;
  readonly onDelete?: (id: string) => void;
  readonly onRefresh: () => void;
  readonly onCancel?: () => void;
  readonly emptyTitle: string;
  readonly classOptions?: readonly { readonly id: string; readonly name: string }[];
}) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ALL');
  const [classId, setClassId] = useState('ALL');
  const [order, setOrder] = useState('NEWEST');
  const [page, setPage] = useState(1);
  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return rows
      .filter(
        (row) =>
          (status === 'ALL' || row.status.key === status) &&
          (classId === 'ALL' || row.classId === classId) &&
          `${row.title} ${row.className ?? ''}`.toLocaleLowerCase().includes(query),
      )
      .sort((left, right) => {
        const a = left.date ? Date.parse(left.date) : NaN;
        const b = right.date ? Date.parse(right.date) : NaN;
        if (!Number.isFinite(a)) return Number.isFinite(b) ? 1 : 0;
        if (!Number.isFinite(b)) return -1;
        return order === 'NEWEST' ? b - a : a - b;
      });
  }, [rows, status, classId, search, order]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages);
  const reset = () => {
    setSearch('');
    setStatus('ALL');
    setClassId('ALL');
    setPage(1);
  };

  return (
    <div className="homework-list" aria-busy={loading}>
      <div className="homework-list__intro">
        <p>{description}</p>
        <span>
          共 <strong>{rows.length}</strong> 份作业
        </span>
      </div>
      <div className="homework-list__tabs" role="group" aria-label="按作业状态筛选">
        {[
          { key: 'ALL', label: '全部', tone: 'neutral' },
          ...statuses.filter(
            (item) => item.key !== 'UNKNOWN' || rows.some((row) => row.status.key === 'UNKNOWN'),
          ),
        ].map((item) => (
          <button
            key={item.key}
            type="button"
            aria-pressed={status === item.key}
            onClick={() => {
              setStatus(item.key);
              setPage(1);
            }}
          >
            {item.label}
            <span>
              {item.key === 'ALL'
                ? rows.length
                : rows.filter((row) => row.status.key === item.key).length}
            </span>
          </button>
        ))}
      </div>
      <div className="homework-list__filters">
        <label className="homework-list__search">
          <span>搜索作业</span>
          <input
            type="search"
            placeholder={classOptions ? '输入作业名称或班级' : '输入作业名称'}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </label>
        {classOptions ? (
          <label>
            <span>所属班级</span>
            <select
              value={classId}
              onChange={(event) => {
                setClassId(event.target.value);
                setPage(1);
              }}
            >
              <option value="ALL">全部班级</option>
              {classOptions.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label>
          <span>日期排序</span>
          <select
            value={order}
            onChange={(event) => {
              setOrder(event.target.value);
              setPage(1);
            }}
          >
            <option value="NEWEST">最新在前</option>
            <option value="OLDEST">最早在前</option>
          </select>
        </label>
      </div>
      {error}
      <div className="homework-list__body">
        <div className="homework-list__columns" aria-hidden="true">
          <span>作业信息</span>
          <span>{dateLabel}</span>
          <span>状态与进度</span>
          <span>操作</span>
        </div>
        {loading ? (
          <div className="empty-panel" role="status">
            <strong>正在加载作业…</strong>
            <span>正在读取日期与最新进度。</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-panel">
            <strong>{rows.length === 0 ? emptyTitle : '没有符合条件的作业'}</strong>
            <span>
              {rows.length === 0 ? '教师布置的作业会显示在这里。' : '试试其他名称、班级或状态。'}
            </span>
            {rows.length > 0 ? (
              <button type="button" onClick={reset}>
                清除筛选
              </button>
            ) : null}
          </div>
        ) : (
          <div className="homework-list__rows">
            {filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((row) => (
              <article className="homework-list__row" key={row.id} data-status={row.status.key}>
                <div className="homework-list__identity">
                  {row.className ? <small>{row.className}</small> : null}
                  <h3>{row.title}</h3>
                  <p>{row.metadata}</p>
                  {row.dueAt ? (
                    <div className="homework-list__due">
                      <span>截止</span>
                      <HomeworkDate value={row.dueAt} label="截止日期" />
                    </div>
                  ) : null}
                </div>
                <div className="homework-list__date">
                  <span className="homework-list__mobile-label">{dateLabel}</span>
                  <HomeworkDate value={row.date} label={dateLabel} />
                </div>
                <div className="homework-list__progress">
                  <div className="homework-list__badges">
                    <span
                      className={`homework-list__badge homework-list__badge--${row.status.tone}`}
                    >
                      {row.status.label}
                    </span>
                    {row.lifecycle ? <small>{row.lifecycle}</small> : null}
                  </div>
                  {row.progress ? (
                    <>
                      <small>{row.progress.label}</small>
                      {row.progress.total > 0 ? (
                        <progress
                          aria-label={`${row.title}：${row.progress.label}`}
                          max={row.progress.total}
                          value={row.progress.value}
                        />
                      ) : null}
                    </>
                  ) : (
                    <small>等待获取批改进度</small>
                  )}
                </div>
                <div className="homework-list__actions">
                  <button
                    type="button"
                    className={`homework-list__open${row.status.tone === 'green' ? ' is-complete' : ''}`}
                    aria-label={`${row.action}：${row.title}`}
                    disabled={busy}
                    onClick={() => onOpen(row.id)}
                  >
                    {row.action}
                    <span aria-hidden="true">→</span>
                  </button>
                  {onDelete ? (
                    <button
                      type="button"
                      className="homework-list__delete"
                      aria-label={`删除作业：${row.title}`}
                      disabled={busy}
                      onClick={() => onDelete(row.id)}
                    >
                      删除
                    </button>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      <footer className="homework-list__footer">
        <span className="homework-list__count" role="status">
          {loading ? '加载中' : `筛选结果 ${filtered.length} 份`}
        </span>
        <nav className="homework-list__pagination" aria-label="作业列表分页">
          <button
            type="button"
            disabled={loading || currentPage <= 1}
            onClick={() => setPage(currentPage - 1)}
          >
            上一页
          </button>
          <span>
            {currentPage} / {pages}
          </span>
          <button
            type="button"
            disabled={loading || currentPage >= pages}
            onClick={() => setPage(currentPage + 1)}
          >
            下一页
          </button>
        </nav>
        <div className="homework-list__footer-actions">
          {onCancel ? (
            <button className="return-button" type="button" onClick={onCancel}>
              返回
            </button>
          ) : null}
          <button type="button" disabled={busy} onClick={onRefresh}>
            {error ? '重新加载' : '刷新列表'}
          </button>
        </div>
      </footer>
    </div>
  );
}
